import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Mail, PlayCircle, PauseCircle, ExternalLink, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Event, EventCampaign } from '@/types/events';

interface EventEmailsInfoProps {
  event: Event;
  brandId: string;
}

export function EventEmailsInfo({ event, brandId }: EventEmailsInfoProps) {
  const navigate = useNavigate();

  // Fetch campaigns linked to this event
  const { data: linkedCampaigns = [], isLoading: isLoadingCampaigns, refetch: refetchCampaigns } = useQuery({
    queryKey: ['event-campaigns-for-event', event.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('os_event_campaigns')
        .select('*')
        .eq('event_id', event.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      
      // Get email counts for each campaign
      const campaignsWithCounts = await Promise.all(
        (data || []).map(async (campaign) => {
          const { count } = await supabase
            .from('os_event_campaign_emails')
            .select('*', { count: 'exact', head: true })
            .eq('campaign_id', campaign.id);
          return { ...campaign, emailCount: count || 0 };
        })
      );
      
      return campaignsWithCounts as (EventCampaign & { emailCount: number })[];
    },
  });

  const handleToggleCampaignStatus = async (campaign: EventCampaign & { emailCount: number }) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    const { error } = await supabase
      .from('os_event_campaigns')
      .update({ status: newStatus })
      .eq('id', campaign.id);
    
    if (error) {
      toast.error('Failed to update campaign status');
    } else {
      toast.success(`Campaign ${newStatus === 'active' ? 'started' : 'paused'}`);
      refetchCampaigns();
    }
  };

  const getCampaignTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      invitation: 'Invitation',
      reminder: 'Reminder',
      pre_event: 'Pre-Event',
      post_event: 'Post-Event',
    };
    return labels[type] || type;
  };

  if (isLoadingCampaigns) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Linked Campaigns Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                Email Campaigns
              </CardTitle>
              <CardDescription>
                Automated email campaigns linked to this event
              </CardDescription>
            </div>
            <Button onClick={() => navigate('/events/campaigns')}>
              <Plus className="h-4 w-4 mr-2" />
              Manage Campaigns
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {linkedCampaigns.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Mail className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No campaigns linked</h3>
              <p className="text-muted-foreground text-center mb-4 max-w-md">
                Create a campaign and link it to this event to automate email sequences.
              </p>
              <Button onClick={() => navigate('/events/campaigns')}>
                <Plus className="h-4 w-4 mr-2" />
                Go to Campaigns
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Emails</TableHead>
                  <TableHead className="w-[150px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {linkedCampaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell className="font-medium">{campaign.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{getCampaignTypeLabel(campaign.campaign_type)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'}>
                        {campaign.status === 'active' ? 'Active' : 'Paused'}
                      </Badge>
                    </TableCell>
                    <TableCell>{campaign.emailCount} emails</TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <div className="flex items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button onClick={() => handleToggleCampaignStatus(campaign)}>
                                {campaign.status === 'active' ? (
                                  <PauseCircle className="w-4 h-4 cursor-pointer hover:opacity-70 text-primary" />
                                ) : (
                                  <PlayCircle className="w-4 h-4 cursor-pointer hover:opacity-70 text-muted-foreground" />
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>{campaign.status === 'active' ? 'Pause' : 'Start'}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <ExternalLink
                                className="w-4 h-4 cursor-pointer hover:opacity-70 text-foreground"
                                onClick={() => navigate(`/events/campaigns/${campaign.id}/emails`)}
                              />
                            </TooltipTrigger>
                            <TooltipContent>View Emails</TooltipContent>
                          </Tooltip>
                        </div>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
